import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { RefreshCw, Link2, Unlink, Download, Loader2, AlertTriangle, School } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, DataTable } from "../components/bits";
import { Button } from "../components/ui/button";

const KIND_LABEL = { course: "Clase", tarea: "Tarea", material: "Material", anuncio: "Anuncio" };
const KIND_STYLE = {
  course: "bg-sky-50 text-sky-700 border-sky-200",
  tarea: "bg-blue-50 text-blue-700 border-blue-200",
  material: "bg-emerald-50 text-emerald-700 border-emerald-200",
  anuncio: "bg-amber-50 text-amber-700 border-amber-200",
};

export default function Classroom() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const [params, setParams] = useSearchParams();

  const load = useCallback(() => {
    api.get("/integrations/google-classroom/status").then((r) => setStatus(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/classroom/items").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (params.get("connected")) {
      toast.success("Cuenta de Google Classroom conectada");
      setParams({}, { replace: true });
    }
    load();
  }, [load, params, setParams]);

  if (!status) return <p className="text-slate-400 text-sm">Cargando integración…</p>;

  const connect = async () => {
    setBusy("connect");
    try {
      const r = await api.get("/integrations/google-classroom/connect");
      window.location.assign(r.data.url);
    } catch (e) {
      toast.error(formatApiError(e));
      setBusy("");
    }
  };

  const sync = async () => {
    setBusy("sync");
    try {
      const r = await api.post("/integrations/google-classroom/sync");
      toast.success(`Sincronización completa: ${r.data.courses} clases, ${r.data.items} elementos`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const importTasks = async () => {
    setBusy("import");
    try {
      const r = await api.post("/classroom/import-assignments");
      toast.success(r.data.created > 0 ? `${r.data.created} tareas importadas a EduNexa` : "No hay tareas nuevas por importar");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (!window.confirm("¿Desconectar Google Classroom? Se eliminarán los elementos sincronizados.")) return;
    try {
      await api.delete("/integrations/google-classroom");
      toast.success("Cuenta desconectada");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="classroom-page">
      <PageHeader title="Google Classroom" subtitle="Conecta tu cuenta y sincroniza clases, tareas, materiales y anuncios sin duplicar trabajo" />

      {!status.configured && (
        <div className="mb-5 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800" data-testid="classroom-not-configured">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">Integración pendiente de configuración</p>
            <p className="mt-1">La plataforma aún no tiene registradas las credenciales de Google Cloud (Client ID y Client Secret). El superadministrador puede registrarlas en <span className="font-medium">Mi Empresa → Integración Google Classroom</span> y la conexión se habilita al instante.</p>
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm mb-6 flex flex-wrap items-center gap-4" data-testid="classroom-status-card">
        <div className="w-12 h-12 rounded-2xl bg-blue-800/10 text-blue-800 flex items-center justify-center">
          <School className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-48">
          {status.connected ? (
            <>
              <p className="font-semibold text-slate-900" data-testid="classroom-connected-email">Conectado como {status.email}</p>
              <p className="text-xs text-slate-500">Última sincronización: {status.last_sync_at ? status.last_sync_at.slice(0, 16).replace("T", " ") : "nunca"}</p>
            </>
          ) : (
            <>
              <p className="font-semibold text-slate-900">Cuenta no conectada</p>
              <p className="text-xs text-slate-500">Conecta tu cuenta de Google para traer tus clases y tareas.</p>
            </>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {!status.connected ? (
            <Button className="bg-blue-800 hover:bg-blue-700" data-testid="classroom-connect" disabled={!status.configured || busy === "connect"} onClick={connect}>
              {busy === "connect" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Link2 className="w-4 h-4 mr-1" />}
              Conectar mi cuenta de Google
            </Button>
          ) : (
            <>
              <Button variant="outline" data-testid="classroom-sync" disabled={busy === "sync"} onClick={sync}>
                {busy === "sync" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Sincronizar ahora
              </Button>
              <Button variant="outline" data-testid="classroom-import" disabled={busy === "import"} onClick={importTasks}>
                {busy === "import" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                Importar tareas a EduNexa
              </Button>
              <Button variant="ghost" className="text-rose-600" data-testid="classroom-disconnect" onClick={disconnect}>
                <Unlink className="w-4 h-4 mr-1" /> Desconectar
              </Button>
            </>
          )}
        </div>
      </div>

      <h2 className="text-lg font-semibold text-slate-800 mb-3">Elementos sincronizados ({items.length})</h2>
      <DataTable testid="classroom-items-table" searchable searchPlaceholder="Buscar clase, tarea, material…" rows={items} columns={[
        { key: "kind", label: "Tipo", render: (i) => <span className={`text-xs font-medium px-2.5 py-0.5 rounded-full border ${KIND_STYLE[i.kind] || ""}`}>{KIND_LABEL[i.kind] || i.kind}</span> },
        { key: "title", label: "Título", render: (i) => i.link ? <a href={i.link} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline font-medium">{i.title || "Sin título"}</a> : <span className="font-medium text-slate-900">{i.title || "Sin título"}</span> },
        { key: "course_name", label: "Clase" },
        { key: "source_updated_at", label: "Actualizado", render: (i) => <span className="text-xs text-slate-500">{(i.source_updated_at || "").slice(0, 10) || "—"}</span> },
      ]} />
    </div>
  );
}
